/**
 * Refund dialog (gdn-2978): give held credit back to the patient,
 * anchored to the specific payment being refunded.
 *
 * A scope's credit is an aggregate but refunds execute against a payment,
 * so the biller picks one; the amount caps at min(that payment's
 * un-refunded remainder, the scope's cash-backed credit) — the server
 * enforces the same caps, so the numbers shown are the numbers accepted.
 *
 * The selected payment's provenance decides what actually happens, so
 * there is no method picker: a Stripe payment refunds to the patient's
 * card now, an external payment records a refund the practice already
 * issued. The copy says which — "we will move money" and "we are logging
 * money you already moved" must never be confusable.
 *
 * A Stripe payment is never recorded by hand (gdn-4242). The Stripe
 * webhook records refunds made in the Stripe dashboard itself, and the
 * server refuses `external` on a Stripe payment, so the old "already
 * refunded in Stripe" override (gdn-3256) is gone: its only job is done
 * upstream, and keeping it would count the same refund twice.
 *
 * Mount per open (parent renders conditionally): the idempotency key is
 * generated once per mount, so a retry after an error reuses it — and on
 * the Stripe path the server calls Stripe before any ledger write, so a
 * rejection leaves nothing recorded and the retry is safe.
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  InputAdornment,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';

import {
  type PatientRefundRequest,
  type RefundablePaymentItem,
  usePatientRefundMutation,
  useRefundablePaymentsQuery,
} from '../../store/v2-api';

import { type CreditSource, formatCents } from './rows';

// Pydantic emits defaulted response fields as not-required, so codegen types
// every amount on a refundable payment as optional. Absent means zero.
const remainderOf = (payment: RefundablePaymentItem): number =>
  payment.refundable_remainder_cents ?? 0;
const refundedOf = (payment: RefundablePaymentItem): number =>
  payment.refunded_cents ?? 0;

export interface RecordRefundDialogProps {
  patientId: string;
  source: CreditSource;
  onClose: () => void;
  /** Fired when the user confirms (the interaction), before the POST. */
  onSubmitted?: (payload: PatientRefundRequest) => void;
  /** Fired after the server accepts the refund. */
  onRecorded?: (payload: PatientRefundRequest) => void;
}

export const RecordRefundDialog: React.FC<RecordRefundDialogProps> = ({
  patientId,
  source,
  onClose,
  onSubmitted,
  onRecorded,
}) => {
  // One idempotency key per dialog open — stable across retries.
  const [refundId] = useState(() => crypto.randomUUID());
  const [paymentId, setPaymentId] = useState<string | null>(null);
  const [amountText, setAmountText] = useState<string>('');
  const [note, setNote] = useState('');
  const [errorText, setErrorText] = useState<string | null>(null);

  const {
    data,
    isLoading: isLoadingPayments,
    isError: paymentsFailed,
    refetch: refetchPayments,
  } = useRefundablePaymentsQuery({
    patientId,
    // Must be undefined, not null, for the account-level scope: RTK Query
    // strips undefined params but serialises null as the string "null".
    sourceEncounterId: source.sourceEncounterId ?? undefined,
  });
  const [postRefund, { isLoading: isPosting }] = usePatientRefundMutation();

  const payments = useMemo(() => data?.payments ?? [], [data]);
  // Both provenances are refundable now (slice 3); what differs is what
  // the confirm does, not whether it is allowed.
  const refundable = (payment: RefundablePaymentItem): boolean =>
    remainderOf(payment) > 0;

  const selected = useMemo(
    () =>
      payments.find(
        payment => payment.payment_ledger_entry_id === paymentId,
      ) ?? null,
    [payments, paymentId],
  );
  const capCents = selected
    ? Math.min(remainderOf(selected), data?.available_credit_cents ?? 0)
    : null;
  const heldBackCents =
    selected && capCents !== null ? remainderOf(selected) - capCents : 0;

  // Preselect the single refundable payment; default the amount to the cap
  // whenever the selection changes.
  useEffect(() => {
    if (paymentId !== null || payments.length === 0) return;
    const enabled = payments.filter(refundable);
    if (enabled.length === 1) {
      setPaymentId(enabled[0].payment_ledger_entry_id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payments]);
  useEffect(() => {
    if (capCents !== null) setAmountText((capCents / 100).toFixed(2));
  }, [capCents]);

  const amountCents = parseDollarsToCents(amountText);
  const amountValid =
    amountCents !== null &&
    amountCents > 0 &&
    capCents !== null &&
    amountCents <= capCents;
  /** Does confirming move money? Only for a Stripe payment, which refunds
   * to the card; any other payment records a refund issued elsewhere. */
  const sendsMoneyNow = selected?.provenance === 'stripe';

  const handleConfirm = async () => {
    if (!selected || !amountValid || amountCents === null) return;
    const payload: PatientRefundRequest = {
      refund_id: refundId,
      payment_ledger_entry_id: selected.payment_ledger_entry_id,
      amount_cents: amountCents,
      // The payment decides the method — a Stripe payment refunds through
      // Stripe, an external one records a refund issued elsewhere. The
      // server models both sides as the same pair, so this is a carry, not
      // a mapping, and it refuses 'external' on a Stripe payment (gdn-4242).
      method: selected.provenance,
      ...(note.trim() ? { note: note.trim() } : {}),
    };
    onSubmitted?.(payload);
    setErrorText(null);
    try {
      await postRefund({
        patientId,
        patientRefundRequest: payload,
      }).unwrap();
      onRecorded?.(payload);
    } catch (error) {
      const message = (error as { data?: { message?: string } })?.data?.message;
      if (sendsMoneyNow) {
        // Whatever the server's reason, the biller's first question after a
        // failed card refund is whether money moved. The server calls Stripe
        // before any ledger write, so the answer is always no — say it, and
        // never let it depend on which message came back.
        const reason = message ?? 'Could not refund the card';
        // Server messages carry no trailing punctuation, so supply the
        // sentence break rather than running the two together.
        const punctuated = /[.!?]$/.test(reason) ? reason : `${reason}.`;
        setErrorText(
          `${punctuated} Nothing was recorded — no money left the account.`,
        );
      } else {
        setErrorText(
          message ??
            'Could not record the refund. The balance may have changed — close and try again.',
        );
      }
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontSize: '1rem', fontWeight: 700 }}>
        Refund a payment
      </DialogTitle>
      <DialogContent>
        {isLoadingPayments ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={22} aria-label="Loading payments" />
          </Box>
        ) : paymentsFailed ? (
          // A failed fetch must never render as the empty state: that
          // states the credit's provenance rules out a refund, which is a
          // claim we cannot make when we never got the payments back.
          <Box>
            <Typography variant="body2" sx={{ color: 'error.main' }}>
              Could not load refundable payments. The refund was not
              recorded.
            </Typography>
            <Button
              size="small"
              onClick={() => refetchPayments?.()}
              sx={{ mt: 1, textTransform: 'none' }}
            >
              Try again
            </Button>
          </Box>
        ) : payments.length === 0 ? (
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            No refundable payments at this scope — the credit here came from
            adjustments or transfers rather than payments that can be given
            back.
          </Typography>
        ) : (
          <>
            <Typography variant="body2" sx={{ mb: 1.5, color: 'text.secondary' }}>
              {source.label} holds a{' '}
              {formatCents(source.creditCents + source.heldBackCents)} credit.
              Choose the payment being refunded:
            </Typography>
            <RadioGroup
              aria-label="Payment being refunded"
              value={paymentId ?? ''}
              onChange={event => {
                setPaymentId(event.target.value);
                setErrorText(null);
              }}
            >
              {payments.map(payment => {
                const disabled = !refundable(payment);
                return (
                  <FormControlLabel
                    key={payment.payment_ledger_entry_id}
                    value={payment.payment_ledger_entry_id}
                    disabled={disabled}
                    control={<Radio size="small" />}
                    label={
                      <Box>
                        <Box
                          sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {payment.date ?? 'No date'} ·{' '}
                            {formatCents(payment.amount_cents)}
                          </Typography>
                          <Chip
                            size="small"
                            label={
                              payment.provenance === 'stripe'
                                ? 'Stripe'
                                : 'External'
                            }
                            sx={{ height: 18, fontSize: '0.625rem' }}
                          />
                        </Box>
                        {refundedOf(payment) > 0 && (
                          <Typography
                            variant="caption"
                            sx={{ color: 'text.secondary', display: 'block' }}
                          >
                            {formatCents(refundedOf(payment))} already
                            refunded
                          </Typography>
                        )}
                      </Box>
                    }
                    aria-label={refundOptionAriaLabel(payment)}
                    sx={{ alignItems: 'flex-start', mb: 0.75 }}
                  />
                );
              })}
            </RadioGroup>
            {selected && capCents !== null && (
              <>
                {heldBackCents > 0 && (
                  // The cap is the scope's *aggregate* free credit, which
                  // open charges, credit already applied, and earlier
                  // refunds all reduce — so state the constraint rather
                  // than blaming one cause the numbers may not support.
                  <Typography
                    variant="body2"
                    sx={{ mt: 0.5, color: 'text.secondary' }}
                  >
                    This payment has{' '}
                    {formatCents(remainderOf(selected))} left to
                    refund, but the account has only {formatCents(capCents)} of
                    credit still available.
                  </Typography>
                )}
                <TextField
                  label="Refund amount"
                  size="small"
                  fullWidth
                  value={amountText}
                  onChange={event => {
                    setAmountText(event.target.value);
                    setErrorText(null);
                  }}
                  error={!amountValid && amountText !== ''}
                  helperText={
                    !amountValid && amountText !== ''
                      ? `Enter an amount between $0.01 and ${formatCents(capCents)}`
                      : `Up to ${formatCents(capCents)}`
                  }
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">$</InputAdornment>
                      ),
                    },
                    htmlInput: {
                      inputMode: 'decimal',
                      'aria-label': `Refund amount in dollars, up to ${formatCents(capCents)}`,
                    },
                  }}
                  sx={{ mt: 1.5 }}
                />
                <TextField
                  label="Note (optional)"
                  size="small"
                  fullWidth
                  value={note}
                  onChange={event => setNote(event.target.value)}
                  slotProps={{ htmlInput: { maxLength: 200 } }}
                  sx={{ mt: 1.5 }}
                />
                {/* The two paths are materially different actions — say
                    which one this confirm performs. */}
                <Typography variant="body2" sx={{ mt: 1.5, fontWeight: 600 }}>
                  {(() => {
                    const amount =
                      amountValid && amountCents !== null ? formatCents(amountCents) : null;
                    if (sendsMoneyNow) {
                      return amount
                        ? `Refunds ${amount} to the card the patient paid with.`
                        : 'Refunds to the card the patient paid with.';
                    }
                    return amount
                      ? `Records a ${amount} refund issued outside Stripe.`
                      : 'Records a refund issued outside Stripe.';
                  })()}{' '}
                  <Typography
                    component="span"
                    variant="body2"
                    sx={{ color: 'text.secondary', fontWeight: 400 }}
                  >
                    {sendsMoneyNow
                      ? 'Card refunds usually take 5–10 business days to reach the patient.'
                      : 'No money moves now — the ledger records what you already paid out.'}
                  </Typography>
                </Typography>
                {/* Where the "already refunded in Stripe" checkbox used to
                    be (gdn-3256, removed in gdn-4242): say where such a
                    refund goes instead of leaving the biller to look. */}
                {sendsMoneyNow && (
                  <Typography
                    variant="caption"
                    sx={{ display: 'block', mt: 1, color: 'text.secondary' }}
                  >
                    Refunds made in the Stripe dashboard are recorded automatically.
                  </Typography>
                )}
              </>
            )}
          </>
        )}
        {errorText && (
          <Alert severity="error" sx={{ mt: 1.5 }}>
            {errorText}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!selected || !amountValid || isPosting}
          onClick={() => void handleConfirm()}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          {sendsMoneyNow
            ? isPosting
              ? 'Refunding…'
              : 'Refund to card'
            : isPosting
              ? 'Recording…'
              : 'Record refund'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const refundOptionAriaLabel = (payment: RefundablePaymentItem): string => {
  const base = `${payment.provenance === 'stripe' ? 'Stripe' : 'External'} payment of ${formatCents(
    payment.amount_cents,
  )}${payment.date ? ` on ${payment.date}` : ''}`;
  const suffix =
    payment.provenance === 'stripe'
      ? ' — refunds to the card'
      : ' — records a refund issued elsewhere';
  if (refundedOf(payment) > 0) {
    return `${base}${suffix}, ${formatCents(refundedOf(payment))} already refunded`;
  }
  return `${base}${suffix}`;
};

/** "85.75" / "$85.75" / "85" → cents; null when unparseable. */
const parseDollarsToCents = (text: string): number | null => {
  const cleaned = text.replace(/[$,\s]/g, '');
  if (!/^\d+(\.\d{1,2})?$/.test(cleaned)) return null;
  return Math.round(parseFloat(cleaned) * 100);
};
